/*
 * Copyright (C) 2026 Nex4Nova (Geoblast group)
 * SPDX-License-Identifier: Proprietary
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import Sidebar from '../Sidebar/Sidebar.jsx';
import { ZoomProvider } from '../../contexts/ZoomContext.jsx';
import { ProjectDataProvider, useProjectData } from '../../contexts/ProjectDataContext.jsx';
import { ThemeProvider } from '../../contexts/ThemeContext.jsx';
import { LanguageProvider } from '../../contexts/LanguageContext.jsx';
import { AnalyticsSelectionsProvider } from '../../contexts/AnalyticsSelectionsContext.jsx';
import { StripLogSelectionsProvider } from '../../contexts/StripLogSelectionsContext.jsx';
import { isTauri } from '../../lib/projectIo.js';

const LAST_DEMO_KEY = 'nex4nova-drilling-last-demo';
const DEMO_CSV = {
  'demo-nex4nova': 'test-data/demo-nex4nova',
  'demo-yilgarn': 'test-data/demo-yilgarn',
  'demo-forrestania': 'test-data/demo-forrestania',
};

function AutoLoadDemo() {
  const { openProjectFromFileList, collars } = useProjectData();
  const navigate = useNavigate();
  // The effect below runs once ([] deps) but needs the collars *after* the
  // demo has been ingested — keep the latest value in a ref.
  const collarsRef = useRef(collars);
  useEffect(() => { collarsRef.current = collars; }, [collars]);

  useEffect(() => {
    if (isTauri()) return;

    let autoLoadEnabled = true;
    try { autoLoadEnabled = localStorage.getItem('nex4nova-drilling-autoload') !== 'false'; } catch (e) { /* ignore */ }
    if (!autoLoadEnabled) return;

    let cancelled = false;

    (async () => {
      try {
        let lastDemo = 'demo-nex4nova';
        try { lastDemo = localStorage.getItem(LAST_DEMO_KEY) || 'demo-nex4nova'; } catch (e) { /* ignore */ }

        const demoPath = DEMO_CSV[lastDemo];
        if (!demoPath) return;

        const base = window.location.origin;
        const csvNames = ['collars', 'survey', 'assays', 'geology', 'structure', 'surface_samples'];
        const files = [];
        for (const name of csvNames) {
          const resp = await fetch(`${base}/${demoPath}/${name}.csv`);
          if (resp.ok) {
            const text = await resp.text();
            files.push(new File([text], `${name}.csv`, { type: 'text/csv' }));
          }
        }
        if (!files.length) return;

        if (cancelled) return;
        await openProjectFromFileList(files, { rename: false });

        if (!cancelled) {
          // Focus the demo's first real collar — DH001 only exists in
          // demo-nex4nova, so derive the id from the loaded collars.
          const firstHoleId = collarsRef.current[0]?.holeId || 'DH001';
          navigate('/strip-log', { state: { holeId: firstHoleId }, replace: true });
        }
      } catch (e) {
        console.warn('Could not auto-load demo:', e);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

function Layout({ children }) {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <ZoomProvider>
          <ProjectDataProvider>
            <AutoLoadDemo />
            <AnalyticsSelectionsProvider>
              <StripLogSelectionsProvider>
                <div className="app-container">
                  <Sidebar />
                  <main className="main-content">{children}</main>
                </div>
              </StripLogSelectionsProvider>
            </AnalyticsSelectionsProvider>
          </ProjectDataProvider>
        </ZoomProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default Layout;
